import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useT } from "../i18n";
import { Zeichen } from "../bausteine/Zeichen";
import { useSitzung } from "./Sitzung";
import { useRuf } from "./Ruf";
import { useAktionsgruppen, useNeuGruppen } from "./Anlegeblatt";
import { ThemaLeiste } from "./Leiste";
import type { Anlegegriff, Anlegegruppe } from "./anlegen";

/* ══════════════════════════════════════════════════════════════════
   Die Werkzeuge im Kopf des Schreibtischs (07.10.2026, Signal, Paket 3,
   docs/64-designsystem-signal.md).

   Der Kopf ist seit Signal eine dunkle Leiste über der ganzen Breite, wie
   im Muster (app-design-muster, Schreibtisch, Hülle): links Einklappen und
   Logo, daneben **„Neu"** und der **Blitz**, in der Mitte die Suche, rechts
   die Zeichen und wer angemeldet ist. Der Name der Maske steht nicht mehr
   darin — er steht im Inhalt, über der Maske (`Schale.tsx`).

   - **„Neu"** klappt dieselben Griffe auf, die am Telefon hinter dem Kreis
     liegen (`useNeuGruppen`, `Anlegeblatt.tsx`).
   - **„Aktionen"** (seit Paket 9, vorher ein Blitz ohne Wort) zeigt die
     Handlungen der Seite — und erscheint nur, wenn sie welche anmeldet
     (08.10.2026). Vermieten steht unter „Neu".
   - **Wer angemeldet ist**, stand bis hierher unten links in der
     Seitenleiste, samt Themawahl. Beides ist jetzt im Menü hinter dem
     Kürzel oben rechts — dort sucht man es in jeder Anwendung, und der Fuß
     der Leiste trägt nur noch Fassung und Rechteinhaber.
   ══════════════════════════════════════════════════════════════════ */

/** Ein Menü, das am Knopf hängt: Klick daneben oder `Escape` schließt es.
 *  Der Wert sagt, welches offen ist — „Neu" und „Aktionen" teilen sich einen
 *  Raum, damit ein Griff zum Nachbarn das eine schließt und das andere öffnet. */
function useAufklapp<W extends string | boolean>(zu: W): {
  offen: W;
  setzeOffen: (x: W | ((v: W) => W)) => void;
  raum: React.RefObject<HTMLDivElement | null>;
} {
  const [offen, setzeOffen] = useState<W>(zu);
  const raum = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (offen === zu) return;
    const daneben = (e: MouseEvent) => {
      if (!raum.current?.contains(e.target as Node)) setzeOffen(zu);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setzeOffen(zu); };
    document.addEventListener("mousedown", daneben);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", daneben);
      document.removeEventListener("keydown", esc);
    };
  }, [offen, zu]);
  return { offen, setzeOffen, raum };
}

/** Ein Menü aus Griffgruppen — dasselbe für „Neu" und für „Aktionen". */
function Griffmenue({ id, schild, gruppen, waehlen, klasse }: {
  id: string;
  schild: string;
  gruppen: Anlegegruppe[];
  waehlen: (g: Anlegegriff) => void;
  klasse?: string;
}) {
  const t = useT();
  return (
    <div className={`neu-menue${klasse ? ` ${klasse}` : ""}`} id={id} role="menu" aria-label={schild}>
      {gruppen.map((g) => (
        <div className="neu-gruppe" key={g.titel} role="group" aria-label={g.titel || schild}>
          {g.titel ? <div className="neu-gruppe-titel">{g.titel}</div> : null}
          {g.griffe.map((griff) => (
            <button
              type="button"
              role="menuitem"
              className={`neu-griff${griff.gefahr ? " gefahr" : ""}`}
              key={griff.wort + JSON.stringify(griff.werte ?? {})}
              disabled={Boolean(griff.gesperrt)}
              onClick={() => waehlen(griff)}
            >
              <Zeichen name={griff.zeichen} />
              <span>
                <span className="neu-griff-wort">{t(griff.wort, griff.werte)}</span>
                {/* Gesperrt sagt der Griff, warum (Regel 9) — sonst sein Satz, wenn er einen hat. */}
                {griff.gesperrt
                  ? <span className="neu-griff-satz">{t(griff.gesperrt)}</span>
                  : griff.satz ? <span className="neu-griff-satz">{t(griff.satz)}</span> : null}
              </span>
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * „Neu" und „Aktionen" — links im Kopf, wie im Muster (Paket 9, 07.10.2026).
 *
 * **„Aktionen" ersetzt den Blitz.** Er war ein Zeichen ohne Wort und konnte
 * eines: gleich vermieten. Jetzt ist er beschriftet und zeigt in einer Akte
 * deren Handlungen — was die Maske über `useAnlegegriffe` anmeldet, dieselbe
 * Anmeldung, die am Telefon hinter dem Kreis steht. Meldet die Seite
 * nichts an, gibt es den Knopf nicht (08.10.2026, `useAktionsgruppen`).
 */
export function HausAnlegen() {
  const t = useT();
  const id = useId();
  const gruppen = useNeuGruppen();
  const aktionen = useAktionsgruppen();
  const { offen, setzeOffen, raum } = useAufklapp<"" | "neu" | "aktionen">("");
  const waehlen = (g: Anlegegriff) => { setzeOffen(""); g.tun(); };
  if (!gruppen.length && !aktionen.length) return null;
  return (
    <div className="haus-anlegen" ref={raum}>
      {gruppen.length ? (
        <div className="haus-griff">
          <button
            type="button"
            className="kopf-neu"
            aria-expanded={offen === "neu"}
            aria-controls={`${id}-neu`}
            aria-haspopup="menu"
            title={t("kopf.neuSchild")}
            onClick={() => setzeOffen((x) => (x === "neu" ? "" : "neu"))}
          >
            <Zeichen name="plus" />
            {t("kopf.neu")}
          </button>
          {offen === "neu" ? (
            <Griffmenue id={`${id}-neu`} schild={t("kopf.neuSchild")} gruppen={gruppen} waehlen={waehlen} />
          ) : null}
        </div>
      ) : null}
      {aktionen.length ? (
        <div className="haus-griff">
          <button
            type="button"
            className="kopf-neu kopf-aktionen"
            aria-expanded={offen === "aktionen"}
            aria-controls={`${id}-aktionen`}
            aria-haspopup="menu"
            title={t("kopf.aktionenSchild")}
            aria-label={t("kopf.aktionen")}
            onClick={() => setzeOffen((x) => (x === "aktionen" ? "" : "aktionen"))}
          >
            <Zeichen name="blitz" />
            {/* Bis 1300 px nur der Blitz — sonst stieße das Wort an die Suche in der Mitte. */}
            <span className="kopf-aktionen-wort">{t("kopf.aktionen")}</span>
          </button>
          {offen === "aktionen" ? (
            <Griffmenue
              id={`${id}-aktionen`}
              schild={t("kopf.aktionenSchild")}
              gruppen={aktionen}
              waehlen={waehlen}
              klasse="aktionen-menue"
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** Alles frisch holen, was die Maske zeigt — ohne die Seite neu zu laden.
 *  Am Telefon tut dasselbe das Herunterziehen (`Ziehauffrischen`). */
export function KopfAktualisieren() {
  const t = useT();
  const abfragen = useQueryClient();
  const ruf = useRuf();
  const [laeuft, setzeLaeuft] = useState(false);
  const holen = async () => {
    setzeLaeuft(true);
    try {
      await abfragen.invalidateQueries();
      ruf("aufgefrischt");
    } finally {
      setzeLaeuft(false);
    }
  };
  return (
    <KopfSymbol schild={t("kopf.aktualisieren")} beiKlick={() => void holen()} aus={laeuft}>
      <Zeichen name="aktualisieren" />
    </KopfSymbol>
  );
}

export function KopfHilfe() {
  const t = useT();
  return (
    <Link to="/handbuch" className="kopf-symbol" title={t("kopf.hilfe")} aria-label={t("kopf.hilfe")}>
      <Zeichen name="hilfe" />
    </Link>
  );
}

function KopfSymbol({ schild, beiKlick, aus, children }: {
  schild: string;
  beiKlick: () => void;
  aus?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className="kopf-symbol"
      title={schild}
      aria-label={schild}
      aria-busy={aus || undefined}
      disabled={aus}
      onClick={beiKlick}
    >
      {children}
    </button>
  );
}

/** Wer angemeldet ist — das Kürzel oben rechts, dahinter das eigene Menü. */
export function Benutzermenue() {
  const t = useT();
  const id = useId();
  const { ich, darf, darfAuf, abmelden } = useSitzung();
  const ruf = useRuf();
  const { offen, setzeOffen, raum } = useAufklapp<boolean>(false);
  if (!ich) return null;
  const rollenText = ich.rollen.map((r) => t(`benutzer.rolle${r.charAt(0).toUpperCase()}${r.slice(1)}`));
  const zu = () => setzeOffen(false);
  return (
    <div className="haus-ich" ref={raum}>
      <button
        type="button"
        className="kopf-ich"
        aria-expanded={offen}
        aria-controls={`${id}-menue`}
        aria-label={t("kopf.ich", { name: ich.name })}
        title={ich.name}
        onClick={() => setzeOffen((x) => !x)}
      >
        {ich.kurz}
      </button>
      {offen ? (
        <div className="mein-menue kopf-menue" id={`${id}-menue`}>
          <div className="menue-kopf">
            <span className="wer">{ich.kurz}</span>
            <span>
              <span className="wer-name">{ich.name}</span>
              <span className="wer-rolle menue-mail">{ich.mail}</span>
            </span>
          </div>
          <div className="menue-teil">
            <span className="menue-schild">{t("benutzer.rollen")}</span>
            <div className="menue-rollen">
              {rollenText.map((r) => <span className="marke" key={r}>{r}</span>)}
            </div>
          </div>
          {/* Hell und Dunkel: seit Signal hier, eine Ebene tiefer als bisher —
              aber in jeder Anwendung dort, wo man es sucht. */}
          <div className="menue-teil">
            <span className="menue-schild">{t("thema.schild")}</span>
            <ThemaLeiste />
          </div>
          {darf(["verwaltung"]) || darfAuf("/einstellungen") ? (
            <Link className="menue-punkt" to="/einstellungen" onClick={zu}>
              {t("benutzer.einstellungen")}
            </Link>
          ) : null}
          <Link className="menue-punkt" to="/handbuch" onClick={zu}>{t("nav.handbuch")}</Link>
          <Link className="menue-punkt" to="/konto" onClick={zu}>{t("nav.konto")}</Link>
          <button
            className="menue-punkt"
            type="button"
            onClick={() => { zu(); void abmelden(); ruf("abgemeldet", undefined, "acht"); }}
          >
            {t("benutzer.abmelden")}
          </button>
        </div>
      ) : null}
    </div>
  );
}
